<template>
  <div class="opportunity-list">
    <!-- 商机列表（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="商机名称/编号" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，点开选择框选值或清除 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.currentStage" :data="stageList"
                                 label-column="dictName" code-column="userCode" type="list"
                                 placeholder="当前阶段" @change="resetSearch"/>
        <!-- 普通角色只看得到与自己相关的商机，按负责人再筛一次没有意义，直接不显示 -->
        <mobile-search-select v-if="isSuperAdmin" v-model="searchInfo.ownerEmployeeId" :query="queryEmployeePage" size="small"
                              label-column="employeeName" code-column="id"
                              placeholder="负责人" search-placeholder="职员名称/手机号"
                              @change="resetSearch">
          <!-- 职员按「头像 + 名称 / 手机号」两行显示（预览模板 B） -->
          <template #item="{item}">
            <div class="mss-item mss-item-avatar-row">
              <div class="mss-item-avatar">
        <img v-if="item.iconUrl" :src="$getImageUrl(item.iconUrl)" alt=""/>
        <yq-icon v-else name="element-ui_user"/>
              </div>
              <div class="mss-item-info">
        <div class="mss-item-main">{{ item.employeeName }}</div>
        <div class="mss-item-sub">{{ item.employeePhone || "无联系电话" }}</div>
              </div>
            </div>
          </template>
        </mobile-search-select>
      </div>
    </mobile-list-header>
    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <list-card class="opportunity-item" v-for="item in dataList" :key="item.id" :label="item.opportunityTitle" @click="toDetail(item)">
        <template #desc>
          <span class="stage-tag" :class="stageClass(item.currentStage)">{{ stageName(item.currentStage) }}</span>
          <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <data-column label="商机编号">{{ item.opportunityNo || "-" }}</data-column>
        <data-column label="商机金额">{{ money(item.opportunityAmount) }}</data-column>
        <data-column label="成交概率">{{ (item.winRate || 0) + "%" }}</data-column>
        <data-column label="预计成交">{{ formatDate(item.expectDealDate) }}</data-column>
        <data-column label="负责人">{{ item.ownerEmployeeName || "-" }}</data-column>
      </list-card>
    </mobile-list>
    <!-- 悬浮「新增商机」按钮：右下角，可拖动 -->
    <mobile-float-button label="新增商机" icon="element-ui_plus" @click="toCreate"/>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import MobileSearchSelect from "@/components/qywx/data/MobileSearchSelect.vue";
import MobileFloatButton from "@/components/qywx/button/MobileFloatButton.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import opportunityApi from "@/api/dz-crm/partner/opportunityApi";
import commonApi from "@/api/commonApi";
import util from "@/util/util";
import {DICT_OPPORTUNITY_STAGE, dictName, stageTagType} from "@/views/h5/crm/crmDictionary";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "OL",
  components: {
    YqIcon, DataColumn, ListCard, MobileFloatButton, MobileSearchSelect, MobileConditionSelect,
    MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        currentStage: "",
        ownerEmployeeId: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      stageList: [], // 商机阶段字典
      // 角色分类，1超级管理员 2普通角色
      roleType: "",
    };
  },
  computed: {
    isSuperAdmin() {
      return this.roleType === 1;
    }
  },
  created() {
    this.roleType = util.getRoleType();
    // 阶段名由字典关联 —— 后端只返回值，不返回名称
    commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => this.stageList = list || []);
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      // 空值不提交：空串绑到后端 Integer 会报错（currentStage 是 Integer）
      const params = {pageNumber: this.searchInfo.pageNumber, pageSize: this.searchInfo.pageSize};
      if (this.searchInfo.searchKey) {
        params.searchKey = this.searchInfo.searchKey;
      }
      if (this.searchInfo.currentStage !== "" && this.searchInfo.currentStage !== null && this.searchInfo.currentStage !== undefined) {
        params.currentStage = this.searchInfo.currentStage;
      }
      if (this.searchInfo.ownerEmployeeId) {
        params.ownerEmployeeId = this.searchInfo.ownerEmployeeId;
      }
      opportunityApi.queryList(params).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    // 重置查询条件，从第一页开始查
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    stageName(stageValue) {
      return dictName(this.stageList, stageValue);
    },
    // 阶段色：字典不给颜色，由前端按固定映射决定（见 crmDictionary 的 STAGE_TAG）
    stageClass(stageValue) {
      const type = stageTagType(stageValue);
      return type ? "tag-" + type : "tag-primary";
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : "-";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return "￥" + Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    queryEmployeePage(params) {
      return commonApi.queryEmployeeListByPage(params);
    },
    toDetail(data) {
      this.router.push(`/qywx/partner/opportunity-list/detail?id=${data.id}`);
    },
    toCreate() {
      this.router.push("/qywx/partner/opportunity-list/detail");
    }
  }
};
</script>

<style lang="scss" scoped>
.opportunity-list{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.opportunity-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  // 阶段标签：字典只给名称，颜色按固定映射
  .stage-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.tag-primary{
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
    }
    &.tag-info{
      color: var(--el-color-info);
      background: var(--el-fill-color-light);
    }
    &.tag-warning{
      color: var(--el-color-warning);
      background: var(--el-color-warning-light-9);
    }
    &.tag-success{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
    &.tag-danger{
      color: var(--el-color-danger);
      background: var(--el-color-danger-light-9);
    }
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
</style>
